import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Trash2, UserPlus, Wrench } from "lucide-react";

const PRODUCTS = ["Air Conditioner", "Air Cooler", "Refrigerator", "Washing Machine", "Other"];

export default function Team() {
  const [engineers, setEngineers] = useState([]);
  const [users, setUsers] = useState([]);
  const [eng, setEng] = useState({ name: "", mobile: "", email: "", zone: "", skill_tags: [] });
  const [usr, setUsr] = useState({ name: "", email: "", password: "", role: "frontdesk", engineer_id: "" });
  const [engOpen, setEngOpen] = useState(false);
  const [usrOpen, setUsrOpen] = useState(false);

  const load = () => {
    api.get("/engineers").then((r) => setEngineers(r.data));
    api.get("/users").then((r) => setUsers(r.data));
  };
  useEffect(load, []);

  const addEngineer = async () => {
    try { await api.post("/engineers", eng); toast.success("Engineer added."); setEngOpen(false); setEng({ name: "", mobile: "", email: "", zone: "", skill_tags: [] }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addUser = async () => {
    try {
      const payload = { ...usr, engineer_id: usr.role === "engineer" ? usr.engineer_id : null };
      await api.post("/users", payload); toast.success("User created."); setUsrOpen(false);
      setUsr({ name: "", email: "", password: "", role: "frontdesk", engineer_id: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delEngineer = async (id) => { await api.delete(`/engineers/${id}`); load(); };
  const delUser = async (id) => { try { await api.delete(`/users/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const toggleSkill = (skill) => setEng((p) => ({
    ...p, skill_tags: p.skill_tags.includes(skill) ? p.skill_tags.filter((s) => s !== skill) : [...p.skill_tags, skill],
  }));

  return (
    <div className="p-5 sm:p-8 fade-up">
      <div className="mb-6">
        <div className="label-caps text-muted-foreground">Administration</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter">Team & Users</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Engineers */}
        <div className="border border-border bg-white">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <div className="flex items-center gap-2 font-heading font-bold"><Wrench size={16} /> Engineers</div>
            <Dialog open={engOpen} onOpenChange={setEngOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-engineer-button" className="rounded-sm bg-klein hover:bg-klein/90 text-white">Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New engineer</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="label-caps">Name</Label><Input value={eng.name} data-testid="eng-name" onChange={(e) => setEng({ ...eng, name: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label className="label-caps">Mobile</Label><Input value={eng.mobile} data-testid="eng-mobile" onChange={(e) => setEng({ ...eng, mobile: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label className="label-caps">Email</Label><Input value={eng.email} onChange={(e) => setEng({ ...eng, email: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label className="label-caps">Zone</Label><Input value={eng.zone} onChange={(e) => setEng({ ...eng, zone: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div>
                    <Label className="label-caps">Skills</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {PRODUCTS.map((p) => (
                        <button key={p} type="button" onClick={() => toggleSkill(p)}
                          className={`text-xs px-2 py-1 border ${eng.skill_tags.includes(p) ? "bg-klein text-white border-klein" : "border-border"}`}>{p}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={addEngineer} data-testid="save-engineer-button" className="rounded-sm bg-klein hover:bg-klein/90 text-white">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-border">
            {engineers.map((e) => (
              <div key={e.id} className="flex items-center justify-between px-5 py-3" data-testid={`engineer-${e.id}`}>
                <div>
                  <div className="font-medium text-sm">{e.name}</div>
                  <div className="text-xs text-muted-foreground">{e.mobile} · {e.zone}</div>
                  <div className="flex gap-1 mt-1 flex-wrap">{e.skill_tags?.map((s) => <span key={s} className="text-[10px] bg-secondary px-1.5 py-0.5">{s}</span>)}</div>
                </div>
                <button onClick={() => delEngineer(e.id)} className="text-signal hover:opacity-70" data-testid={`del-engineer-${e.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* Users */}
        <div className="border border-border bg-white">
          <div className="flex items-center justify-between px-5 py-3 border-b border-border">
            <div className="flex items-center gap-2 font-heading font-bold"><UserPlus size={16} /> Login users</div>
            <Dialog open={usrOpen} onOpenChange={setUsrOpen}>
              <DialogTrigger asChild><Button size="sm" data-testid="add-user-button" className="rounded-sm bg-klein hover:bg-klein/90 text-white">Add</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="label-caps">Name</Label><Input value={usr.name} data-testid="usr-name" onChange={(e) => setUsr({ ...usr, name: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label className="label-caps">Email</Label><Input value={usr.email} data-testid="usr-email" onChange={(e) => setUsr({ ...usr, email: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label className="label-caps">Password</Label><Input type="password" value={usr.password} data-testid="usr-password" onChange={(e) => setUsr({ ...usr, password: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div>
                    <Label className="label-caps">Role</Label>
                    <Select value={usr.role} onValueChange={(v) => setUsr({ ...usr, role: v })}>
                      <SelectTrigger data-testid="usr-role" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="frontdesk">Front Desk</SelectItem>
                        <SelectItem value="engineer">Engineer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {usr.role === "engineer" && (
                    <div>
                      <Label className="label-caps">Link to engineer</Label>
                      <Select value={usr.engineer_id} onValueChange={(v) => setUsr({ ...usr, engineer_id: v })}>
                        <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                        <SelectContent>{engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <DialogFooter><Button onClick={addUser} data-testid="save-user-button" className="rounded-sm bg-klein hover:bg-klein/90 text-white">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between px-5 py-3" data-testid={`user-${u.id}`}>
                <div>
                  <div className="font-medium text-sm">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.role}</div>
                </div>
                {u.role !== "admin" && <button onClick={() => delUser(u.id)} className="text-signal hover:opacity-70" data-testid={`del-user-${u.id}`}><Trash2 size={16} /></button>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
